import { getContainerAfterPos } from 'common/Prose/utils/getContainerAfterPos'
import { selectionToInsertionEnd } from 'common/Prose/utils/selectionToInsertionEnd'
import { ProsePreview } from 'common/ProsePreview/ProsePreview'
import {
  INSERT_SNIPPET,
  REMOVE_SNIPPET_PREVIEW,
  SHOW_SNIPPET_PREVIEW,
  SNIPPET_DATA,
} from 'components/Snippets/Constants'
import { Node, ResolvedPos, Schema } from 'prosemirror-model'
import { Plugin, PluginKey, Transaction } from 'prosemirror-state'
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from 'react-query'
import { showSnippetsRail } from 'redux/actions/snippets'
import store from 'redux/store'
import Styles from './Snippets.module.scss'

export const SnippetsPluginKey = new PluginKey('snippets')

const buildDecorations = (tr: Transaction, nodes: Node[], schema: Schema, queryClient: QueryClient): Decoration[] => {
  const decorations: Decoration<{ [key: string]: any }>[] = []
  const { $from }: { $from: ResolvedPos } = tr.getMeta('$from')
  const from = getContainerAfterPos($from.doc, $from.pos).pos
  decorations.push(
    Decoration.widget(from, () => {
      const div = document.createElement('div')
      const root = createRoot(div)

      root.render(
        <QueryClientProvider client={queryClient}>
          <ProsePreview schema={schema} className={Styles.snippetPreview} nodes={nodes} />
        </QueryClientProvider>,
      )
      return div
    }),
  )

  return decorations
}

const defaultState = (doc: Node) => ({ decorations: DecorationSet.create(doc, []) })

const SnippetsPlugin: (queryClient: QueryClient) => Plugin = (queryClient) =>
  new Plugin({
    key: SnippetsPluginKey,
    state: {
      init: (config) => defaultState(config.doc),
      apply: (tr, state, editorState) => {
        const insertSnippet = tr.getMeta(INSERT_SNIPPET)
        const removeSnippetPreview = tr.getMeta(REMOVE_SNIPPET_PREVIEW)
        const showSnippetPreview = tr.getMeta(SHOW_SNIPPET_PREVIEW)
        const snippetData = tr.getMeta(SNIPPET_DATA)

        if (removeSnippetPreview) {
          return { decorations: DecorationSet.create(tr.doc, []) }
        }

        if (showSnippetPreview) {
          const newDecorations: Decoration[] = buildDecorations(tr, snippetData.nodes, editorState.schema, queryClient)
          return { decorations: DecorationSet.create(tr.doc, newDecorations) }
        }

        if (insertSnippet) {
          return {
            ...state,
            insertSnippet,
            removeSnippet: false,
            snippetData,
            decorations: DecorationSet.create(tr.doc, []),
          }
        }

        return defaultState(state.doc)
      },
    },
    props: {
      decorations(state) {
        return SnippetsPlugin(queryClient).getState(state).decorations
      },
    },
    view: () => {
      return {
        update: (view: EditorView) => {
          const { insertSnippet, snippetData } = SnippetsPluginKey.getState(view.state)

          const handleInsertion = (nodes: Node[]) => {
            const {
              doc,
              tr,
              selection: { from },
            } = view.state

            const $containerAfter = getContainerAfterPos(doc, from)

            tr.insert($containerAfter.pos, nodes)
            selectionToInsertionEnd(tr)

            store.dispatch(showSnippetsRail(false))
            view.dispatch(tr)
            view.focus()
          }

          if (insertSnippet) {
            handleInsertion(snippetData.nodes)
          }
        },
      }
    },
  })

export default SnippetsPlugin
